import React, { useCallback, useEffect } from "react";

import { faWarning } from "@fortawesome/pro-light-svg-icons";
import { useSearchParams } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

type TabListPadding = "px-4" | "none";

export type SearchParamsComponent = {
  searchParamValue: string;
  title: React.ReactNode;
  component: React.ReactNode;
  icon?: React.ReactNode;
  errored?: boolean;
  dataLoc?: string;
  suffix?: React.ReactNode;
  disabled?: boolean | string;
};

export type Props = {
  tabs: SearchParamsComponent[];
  searchParamKey: string;
  defaultValue: string;
  containerClassName?: string;
  selectedTabClassName?: string;
  inactiveTabClassName?: string;
  componentClassName?: string;
  tabListPadding?: TabListPadding;
};

export const SearchParamsTabView: React.FC<Props> = ({
  tabs,
  searchParamKey,
  defaultValue,
  containerClassName,
  selectedTabClassName,
  inactiveTabClassName,
  componentClassName,
  tabListPadding = "px-4",
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // keep-memo(react-compiler): dependency of the default-tab effect below, so
  // an unstable identity would re-run that effect on every render.
  const setParamValue = useCallback(
    (newValue: string) => {
      setSearchParams((prevSearchParams) => {
        prevSearchParams.set(searchParamKey, newValue);
        return prevSearchParams;
      });
    },
    [setSearchParams, searchParamKey],
  );

  const rightPaneParamValue = searchParams.get(searchParamKey);
  const currentValue =
    tabs.find(
      (t: SearchParamsComponent) =>
        t.searchParamValue === rightPaneParamValue && !t.disabled,
    )?.searchParamValue || defaultValue;

  useEffect(() => {
    if (!currentValue) {
      setParamValue(defaultValue);
    }
  }, [currentValue, defaultValue, setParamValue]);

  const getSelectedTab = () => {
    if (!tabs) return;
    const selectedTab = tabs.find(
      (t: SearchParamsComponent) => t.searchParamValue === currentValue,
    );
    return selectedTab ? selectedTab : tabs[0];
  };

  const renderErrored = () => (
    <div className="ml-1.5 rounded-md bg-red-100 px-1.5 py-0.5">
      <Icon icon={faWarning} size="xs" variant="error" />
    </div>
  );

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className={tabListPadding !== "none" ? tabListPadding : undefined}>
        <div
          className={twJoin(
            "flex flex-row items-center font-inter-semibold-14px text-gray-800",
            containerClassName,
          )}
        >
          {tabs?.map((t: SearchParamsComponent) => {
            const isActive = currentValue === t.searchParamValue;
            const isDisabled = Boolean(t.disabled);
            const disabledTooltip =
              typeof t.disabled === "string" ? t.disabled : undefined;

            const tabContent = (
              <div key={t.searchParamValue}>
                <div
                  key={t.searchParamValue}
                  className={twJoin(
                    "flex h-full flex-row items-center px-2 py-2 font-inter-semibold-13px",
                    isActive ? selectedTabClassName : inactiveTabClassName,
                    !isActive && !isDisabled && "cursor-pointer",
                    isDisabled && "cursor-not-allowed opacity-50",
                  )}
                  data-loc={t.dataLoc || t.title}
                  onClick={
                    !isActive && !isDisabled
                      ? () => {
                          setParamValue(t.searchParamValue);
                        }
                      : undefined
                  }
                >
                  {t.title}
                  {t.icon && <div className="ml-1">{t.icon}</div>}
                  {t.errored && renderErrored()}
                  {t.suffix}
                </div>
                <div
                  className={twJoin(
                    "h-1 w-full rounded-full",
                    isActive ? "bg-indigo-600" : "bg-transparent",
                  )}
                ></div>
              </div>
            );

            if (isDisabled && disabledTooltip) {
              return (
                <Tooltip
                  key={t.searchParamValue}
                  title={disabledTooltip}
                  asChild
                >
                  {tabContent}
                </Tooltip>
              );
            }

            return tabContent;
          })}
        </div>
        <div className="-mt-0.5 h-px bg-gray-200"></div>
      </div>
      <div
        className={twJoin(
          "decideScrollbar flex-1 overflow-y-auto",
          componentClassName,
        )}
      >
        {getSelectedTab()?.component}
      </div>
    </div>
  );
};
